import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {AnimatePresence, motion} from 'framer-motion';

import {FaJava, FaReact, FaAws} from "react-icons/fa";
import {SiSpring, SiPostgresql, SiKubernetes, SiApachekafka, SiTailwindcss} from "react-icons/si";

import AboutPage from "./AboutPage";
import {Route, Routes, NavLink, BrowserRouter as Router, useNavigate} from "react-router-dom";
import ContactChat from "./ContactChat";
import ProjectPage from "./ProjectPage";
import ScrollToTop from "./ScrollToTop";

const ASSETS_TO_PRELOAD = [
    "/pr12.mp4",
    "/pr22.mp4",
    "/IMG_7042.mp4",
    "/IMG_7047.JPG"
];

const NavbarWrapper = styled.nav`
    width: 33%;
    height: 50px;
    border-radius: 100px;
    background-color: white;
    position: fixed;
    top: 2px;
    left: 50%;
    margin-top: 40px;
    transform: translateX(-50%);
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    display: flex;
    justify-content: space-around;
    align-items: stretch;
    background-color: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    @media (max-width: 1024px) {
        width: 60%;
    }

    @media (max-width: 768px) {
        width: 90%;
        height: 45px;
        margin-top: 20px;
    }
`;

const StyledLink = styled(NavLink)`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 85%;
    font-size: 16px;
    margin: 4px 3px;
    font-weight: 500;
    text-decoration: none;
    border-radius: 100px;
    transition: color 0.3s, background-color 0.3s;
    color: #999;

    &.active {
        background-color: #f2f2f2;
        color: black;
    }

    &:hover {
        color: black;
    }

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Navbar = () => (
    <NavbarWrapper>
        <StyledLink to="/" end>Home</StyledLink>
        <StyledLink to="/about">About</StyledLink>
        <StyledLink to="/projects">Projects</StyledLink>
        <StyledLink to="/contact">Contact</StyledLink>
    </NavbarWrapper>
);

const Wrapper = styled.div`
    background-color: white;
    padding: 121px 30px 60px;

    max-width: 1400px;
    width: 100%;
    margin: 0 auto;

    display: grid;
    grid-template-columns: repeat(4, 1fr);

    gap: 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    box-sizing: border-box;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        padding: 90px 20px 40px;
        grid-template-columns: 1fr;
        gap: 15px;
    }
`;

const Card = styled.div`
    position: relative;
    background-color: #f7f7f9;
    border-radius: 24px;
    padding: 30px;
    display: flex;
    flex-direction: column;
    text-align: left;

    ${props => props.large && `grid-column: span 2; grid-row: span 2;`}
    ${props => props.long && `grid-column: span 2; grid-row: span 1;`}
    ${props => props.top && `margin-top: 20px;`}
    ${props => props.row && `flex-direction: row; align-items: flex-start;`}

    @media (max-width: 1024px) {
    ${props => props.large && `grid-column: span 2;`}
    ${props => props.long && `grid-column: span 2;`}
}

    @media (max-width: 768px) {
        grid-column: span 1 !important;
        grid-row: auto !important;
        margin-top: 0 !important;

        padding: 24px;

        flex-direction: column !important;
        align-items: center !important;
        text-align: center;
    }
`;
const MotionCard = motion(Card);

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 20px;

    @media (max-width: 768px) {
        margin-left: 0;
        margin-top: 15px;
        align-items: center;
    }
`;

const CardImage = styled.img`
    width: 130px;
    height: 130px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    @media (max-width: 768px) {
        width: 110px;
        height: 110px;
    }
`;

const PlaceholderTitle = styled.h3`
    font-size: 30px;
    font-weight: 620;
    margin-bottom: -1px;

    ${props => props.normal && `
        margin-top: -15px;
        margin-left: 27px;
    `}
    ${props => props.project && `
        margin-top: -15px;
        margin-left: 0px;
    `}

    @media (max-width: 768px) {
    font-size: 24px;
    margin-left: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 10px;
    text-align: center;
}
`;

const FooterWrapper = styled.footer`
    width: 60%;
    text-align: center;
    padding: 20px;
    font-size: 14px;
    color: #777;
    margin-left: auto;
    margin-right: auto;
    margin-top: 45px;
    background-color: transparent;

    @media (max-width: 768px) {
        width: 90%;
        font-size: 13px;
        margin-top: 20px;
    }
`;
const Footer = () => (
    <FooterWrapper>
        © 2026. Hand-Crafted & Made by Emin Jafarli
    </FooterWrapper>
);

const PlaceholderText = styled.p`
    font-size: 16px;
    color: #676666;
    margin-bottom: -5px;
    line-height: 1.5;

    ${props => props.bigtext && `
        font-size: 24px;
    `}

    @media (max-width: 768px) {
    font-size: 15px;
    ${props => props.bigtext && `font-size: 18px;`}
    text-align: center;
}
`;

const SkillsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-top: 20px;
    margin-left: 8px;

    @media (max-width: 768px) {
        margin-left: 0;
        width: 100%;
        gap: 12px;
    }
`;

const Skill = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    color: #676666;

    @media (max-width: 768px) {
        justify-content: flex-start;
        font-size: 14px;
    }
`;

const ProjectList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
    margin-top: 15px;
    width: 100%;
`;

const HoverLabel = styled(motion.div)`
    position: absolute;
    left: 100%;
    bottom: 50%;
    transform: translateY(-50%);
    margin-left: -85px;
    margin-bottom: 28px;
    background: white;
    color: #333;
    padding: 4px 10px;
    font-size: 13px;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    pointer-events: none;
    z-index: 1003;

    @media (hover: none) and (pointer: coarse) {
        display: none;
    }
`;

const Overlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.95);
    z-index: 500;
    pointer-events: none;
`;

const CardArrowButton = styled.button`
    position: absolute;
    bottom: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background-color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: rotate(-45deg);
    }

    svg {
        width: 20px;
        height: 20px;
        transform: rotate(90deg);
    }

    @media (max-width: 768px) {
        bottom: 10px;
        right: 10px;
        width: 32px;
        height: 32px;
        svg {
            width: 16px;
            height: 16px;
        }
    }
`;

const ProjectCard = styled.div`
    margin-top: 15px;
    background-color: #e8e8e8;
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-align: left; 

    &:hover {
        background-color: #dcdcdc;
    }

    h4 {
        margin: 0 0 8px 0;
    }

    p {
        margin: 0;
        color: #444;
        font-size: 14px;
    }
`;

const projectsData = [
    {
        title: "Card Management System",
        description: "CMS is an application that allows users transfer money, search and view details about their cards."
    }
];


const HomePage = () => {
    const navigate = useNavigate();
    const [activeOverlayCard, setActiveOverlayCard] = useState(null);


    return (
        <>
            {activeOverlayCard === 'intro' && (<Overlay initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.2 }}/>)}
            {activeOverlayCard === 'profile' && (
                <Overlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                />
            )}
            {activeOverlayCard === 'project' && (
                <Overlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                />
            )}

            <Wrapper>
                <MotionCard
                    large
                    top
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6}}
                    style={{
                        zIndex: activeOverlayCard === 'intro' ? 1001 : 'auto',
                        position: 'relative',
                    }}
                >
                    <CardArrowButton
                        onClick={() => navigate(`/about`)}
                        onMouseEnter={() => setActiveOverlayCard('intro')}
                        onMouseLeave={() => setActiveOverlayCard(null)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="black">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7L7 17M17 17H7V7" />
                        </svg>

                        <AnimatePresence>
                            {activeOverlayCard === 'intro' && (
                                <HoverLabel
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0, rotate:45 }}
                                    exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    About
                                </HoverLabel>
                            )}
                        </AnimatePresence>
                    </CardArrowButton>


                    <PlaceholderTitle>
                        <span style={{color: '#8F8F8F'}}>Hi, I'm</span> <span style={{color: 'black'}}>Emin Jafarli.</span>
                    </PlaceholderTitle>
                    <PlaceholderText bigtext>
                        I am an information technology student and software developer with a strong foundation
                        in creating reliable and user-friendly web applications. Committed to continuous learning, I
                        approach challenges with analytical problem-solving skills and a dedication to professional growth.
                        My goal is to leverage emerging technologies to deliver effective solutions while advancing my
                        expertise in the field.
                    </PlaceholderText>
                </MotionCard>

                <MotionCard
                    long
                    top
                    row
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 0.1}}
                    style={{
                        zIndex: activeOverlayCard === 'profile' ? 1001 : 'auto',
                        position: 'relative',
                    }}
                >

                    <CardArrowButton
                        onClick={() => navigate(`/contact`)}
                        onMouseEnter={() => setActiveOverlayCard('profile')}
                        onMouseLeave={() => setActiveOverlayCard(null)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="black">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7L7 17M17 17H7V7" />
                        </svg>

                        <AnimatePresence>
                            {activeOverlayCard === 'profile' && (
                                <HoverLabel
                                    style={{ marginLeft: '-95px', marginBottom: '32px' }}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0, rotate: 45}}
                                    exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    Contact
                                </HoverLabel>
                            )}
                        </AnimatePresence>
                    </CardArrowButton>

                    <CardImage src="/emin1.png" alt="Emin"/>
                    <TextWrapper>
                        <PlaceholderTitle>Emin Jafarli</PlaceholderTitle>
                        <PlaceholderText>Full-Stack Software Engineer</PlaceholderText>
                    </TextWrapper>
                </MotionCard>

                <MotionCard
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 0.2}}
                >
                    <PlaceholderTitle normal>Technical Stack</PlaceholderTitle>
                    <SkillsGrid>
                        <Skill><FaJava color="#007396" size={24}/> Java</Skill>
                        <Skill><FaReact color="#61DBFB" size={24}/> React</Skill>
                        <Skill><SiSpring color="#6DB33F" size={24}/> Spring Boot</Skill>
                        <Skill><SiTailwindcss color="#06B6D4" size={24}/> Tailwind</Skill>
                        <Skill><SiPostgresql color="#336791" size={24}/> PostgreSQL</Skill>
                        <Skill><SiApachekafka color="#231F20" size={24}/> Kafka</Skill>
                        <Skill><FaAws color="#FF9900" size={24}/> AWS</Skill>
                        <Skill><SiKubernetes color="#326CE5" size={24}/> Kubernetes</Skill>
                    </SkillsGrid>
                </MotionCard>
                <MotionCard
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 0.3}}
                >
                    <PlaceholderTitle project>Projects</PlaceholderTitle>
                    <ProjectList>
                        {projectsData.map(({title, description}) => (
                            <ProjectCard style={{
                                zIndex: activeOverlayCard === 'project' ? 1001 : 'auto',
                                position: 'relative',
                            }}
                                         key={title}
                                         onClick={() => navigate(`/projects`)}
                                         onMouseEnter={() => setActiveOverlayCard('project')}
                                         onMouseLeave={() => setActiveOverlayCard(null)}>
                                <AnimatePresence>
                                    {activeOverlayCard === 'project' && (
                                        <HoverLabel
                                            style={{ marginLeft: '-162px', marginBottom: '-95px' }}
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 4 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            Projects
                                        </HoverLabel>
                                    )}
                                </AnimatePresence>

                                <h4>{title}</h4>
                                <p>{description}</p>
                            </ProjectCard>
                        ))}
                    </ProjectList>
                </MotionCard>
            </Wrapper>
        </>
    );
};

const GlobalAssetPreloader = () => {
    useEffect(() => {
        ASSETS_TO_PRELOAD.forEach(src => {
            fetch(src)
                .then(res => {
                })
                .catch(err => console.log("Preload failed for", src));
        });
    }, []);

    return null;
};

function App() {
    return (
        <Router>
            <GlobalAssetPreloader />

            <Navbar />
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactChat />} />
                <Route path="/projects" element={<ProjectPage />} />
            </Routes>
            <Footer />
        </Router>
    );
}

export default App;